import { HealthcomInsurance } from '@chiroup/core/types/PatientInsurance.type';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import requestService from '../../services/request.service';

const verbiage = {
  en: {
    title: 'Insurance information',
    description:
      'Tell us about your primary insurance so we can check your coverage before your visit.',
    insuranceName: 'Insurance company',
    memberId: 'Member ID',
    groupNumber: 'Group number (optional)',
    holderName: 'Policyholder name',
    holderRelationship: 'Relationship to policyholder',
    relationshipSelf: 'Self',
    relationshipSpouse: 'Spouse',
    relationshipChild: 'Child',
    relationshipOther: 'Other',
    submit: 'Save and continue',
    skip: 'I don’t have insurance',
    submitting: 'Saving…',
    submittedTitle: 'Insurance saved',
    submittedDetail: 'Thanks. We’ll verify coverage before your visit.',
    continue: 'Continue',
    errorTitle: 'Something went wrong',
    errorDetail: 'Please try again in a moment.',
  },
  es: {
    title: 'Información del seguro',
    description:
      'Cuéntenos sobre su seguro principal para que podamos verificar su cobertura antes de la visita.',
    insuranceName: 'Compañía de seguros',
    memberId: 'Número de miembro',
    groupNumber: 'Número de grupo (opcional)',
    holderName: 'Titular de la póliza',
    holderRelationship: 'Relación con el titular',
    relationshipSelf: 'Yo mismo',
    relationshipSpouse: 'Cónyuge',
    relationshipChild: 'Hijo/a',
    relationshipOther: 'Otro',
    submit: 'Guardar y continuar',
    skip: 'No tengo seguro',
    submitting: 'Guardando…',
    submittedTitle: 'Seguro guardado',
    submittedDetail: 'Gracias.',
    continue: 'Continuar',
    errorTitle: 'Algo salió mal',
    errorDetail: 'Por favor inténtelo de nuevo en unos momentos.',
  },
};

type Props = {
  requestId: string;
  validationCode: string | number;
  language: 'en' | 'es';
  primaryColor: string;
  alreadyComplete?: boolean;
  kiosk?: boolean;
  onComplete: () => void;
};

type Status = 'ready' | 'submitting' | 'submitted' | 'error';

const StepInsurance: React.FC<Props> = ({
  requestId,
  validationCode,
  language,
  primaryColor,
  alreadyComplete,
  kiosk,
  onComplete,
}) => {
  const v = verbiage[language];
  const [status, setStatus] = useState<Status>(alreadyComplete ? 'submitted' : 'ready');
  const [insuranceName, setInsuranceName] = useState('');
  const [memberId, setMemberId] = useState('');
  const [groupNumber, setGroupNumber] = useState('');
  const [holderName, setHolderName] = useState('');
  const [relationship, setRelationship] = useState<'self' | 'spouse' | 'child' | 'other'>('self');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (status === 'submitting') return;

    setStatus('submitting');

    try {
      // The legacy submit endpoint accepts a wider shape than the
      // strict `HealthcomInsurance` type — extra keys like
      // `insuranceName` / `groupNumber` / `holderName` / `relationship`
      // are persisted by the server alongside the canonical fields.
      const insurance: Partial<HealthcomInsurance> & Record<string, unknown> = {
        legalName: insuranceName.trim(),
        memberId: memberId.trim(),
      };

      if (groupNumber.trim()) insurance.groupNumber = groupNumber.trim();
      if (holderName.trim()) insurance.holderName = holderName.trim();
      insurance.relationship = relationship;

      await requestService.insuranceCreate(requestId, validationCode, [insurance], kiosk);
      setStatus('submitted');
    } catch (err) {
      Sentry.captureException(err);
      setStatus('error');
    }
  };

  const handleSkip = async () => {
    if (status === 'submitting') return;

    setStatus('submitting');

    try {
      await requestService.skipInsuranceCreate(requestId, validationCode, kiosk);
      setStatus('submitted');
    } catch (err) {
      Sentry.captureException(err);
      setStatus('error');
    }
  };

  if (status === 'submitted') {
    return (
      <div
        className="rounded-3xl overflow-hidden p-8 text-center flex flex-col items-center gap-3 dark:ring-1 dark:ring-white/10"
        style={{ backgroundColor: `${primaryColor}12` }}
      >
        <div
          className="size-12 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${primaryColor}25`, color: primaryColor }}
          aria-hidden="true"
        >
          <CheckCircleSolid className="size-7" />
        </div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          {v.submittedTitle}
        </h1>
        <p className="max-w-prose text-sm text-gray-600 dark:text-gray-300">{v.submittedDetail}</p>
        <button
          type="button"
          onClick={onComplete}
          className="mt-2 inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-semibold text-white"
          style={{ backgroundColor: primaryColor }}
        >
          {v.continue}
        </button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="portal-card p-6 text-center">
        <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {v.errorTitle}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{v.errorDetail}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="portal-card p-5 sm:p-6 flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          {v.title}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-prose">{v.description}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={v.insuranceName} required>
          <input
            type="text"
            name="insuranceName"
            required
            value={insuranceName}
            onChange={e => setInsuranceName(e.target.value)}
            disabled={status === 'submitting'}
            className="w-full rounded-xl border border-gray-300 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2.5 text-base sm:text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ ['--tw-ring-color' as any]: primaryColor } as React.CSSProperties}
          />
        </Field>

        <Field label={v.memberId} required>
          <input
            type="text"
            name="memberId"
            required
            value={memberId}
            onChange={e => setMemberId(e.target.value)}
            disabled={status === 'submitting'}
            className="w-full rounded-xl border border-gray-300 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2.5 text-base sm:text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ ['--tw-ring-color' as any]: primaryColor } as React.CSSProperties}
          />
        </Field>

        <Field label={v.groupNumber}>
          <input
            type="text"
            name="groupNumber"
            value={groupNumber}
            onChange={e => setGroupNumber(e.target.value)}
            disabled={status === 'submitting'}
            className="w-full rounded-xl border border-gray-300 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2.5 text-base sm:text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ ['--tw-ring-color' as any]: primaryColor } as React.CSSProperties}
          />
        </Field>

        <Field label={v.holderRelationship}>
          <div className="inline-grid w-full grid-cols-[1fr_--spacing(8)]">
            <select
              name="relationship"
              value={relationship}
              onChange={e =>
                setRelationship(e.target.value as 'self' | 'spouse' | 'child' | 'other')
              }
              disabled={status === 'submitting'}
              className="col-span-full row-start-1 appearance-none pr-8 w-full rounded-xl border border-gray-300 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2.5 text-base sm:text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ ['--tw-ring-color' as any]: primaryColor } as React.CSSProperties}
            >
              <option value="self">{v.relationshipSelf}</option>
              <option value="spouse">{v.relationshipSpouse}</option>
              <option value="child">{v.relationshipChild}</option>
              <option value="other">{v.relationshipOther}</option>
            </select>
            <svg
              viewBox="0 0 8 5"
              width="8"
              height="5"
              fill="none"
              className="pointer-events-none col-start-2 row-start-1 place-self-center"
              aria-hidden="true"
            >
              <path d="M.5.5 4 4 7.5.5" stroke="currentcolor" />
            </svg>
          </div>
        </Field>

        {relationship !== 'self' && (
          <Field label={v.holderName} className="sm:col-span-2">
            <input
              type="text"
              name="holderName"
              value={holderName}
              onChange={e => setHolderName(e.target.value)}
              disabled={status === 'submitting'}
              className="w-full rounded-xl border border-gray-300 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2.5 text-base sm:text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ ['--tw-ring-color' as any]: primaryColor } as React.CSSProperties}
            />
          </Field>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-950/5 dark:border-white/10">
        <button
          type="button"
          onClick={handleSkip}
          disabled={status === 'submitting'}
          className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50"
        >
          {v.skip}
        </button>
        <button
          type="submit"
          disabled={status === 'submitting' || !insuranceName.trim() || !memberId.trim()}
          className="inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: primaryColor }}
        >
          {status === 'submitting' ? v.submitting : v.submit}
        </button>
      </div>
    </form>
  );
};

const Field: React.FC<{
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ label, required, className, children }) => (
  <label className={`flex flex-col gap-1.5 ${className ?? ''}`}>
    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
      {label}
      {required && <span aria-hidden="true"> *</span>}
    </span>
    {children}
  </label>
);

export default StepInsurance;
